import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Fab,
  useTheme,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Phone,
  Email,
  WhatsApp,
  Notifications,
  ContactPhone,
  Send,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Chat as ChatIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

import { Contact } from '../../types';
import { contactService } from '../../services/contactService';
import { ContactForm, EnviarWhatsappMsgForm, EnviarEmailForm } from '../forms';
import { formatarPhone } from '../../utils/formatToView';
import { getCountryByNumber } from '../../utils/countries';
import { usePermissions } from '../../contexts/AuthContext';
import { tableHeadRowSx, themedTableContainerSx } from '../../styles/theme';
import { alpha } from '@mui/material/styles';
import { atendimentoService, AtendimentoConexao, AtendimentoContato, AtendimentoTicket } from '../../services/atendimentoService';
import ContatoAtendimentoDetalhesDialog from '../../pages/atendimento/componentes/ContatoAtendimentoDetalhesDialog';

const getInitials = (name?: string) =>
  name ? name.split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase() : '?';
import NovoAtendimentoClienteDialog from '../../pages/atendimento/componentes/NovoAtendimentoClienteDialog';
import SetoresAllSelect from '../../pages/atendimento/componentes/SetoresAllSelect';

const obterDadosPais = (numero: string, ddi?: string | null) => {
  if (ddi) return getCountryByNumber(ddi);
  let limpo = numero.replace(/\D/g, '');
  if (limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    return getCountryByNumber('55');
  }
  if (limpo.length === 10 || limpo.length === 11) {
    return getCountryByNumber('55');
  }
  return getCountryByNumber(limpo);
};

const formatarNumeroComDddParenteses = (numero: string, ddi?: string | null): string => {
  if (!numero) return '';
  let limpo = numero.replace(/\D/g, '');
  
  let ddiEncontrado = ddi || '';
  if (!ddiEncontrado && limpo.startsWith('55') && (limpo.length === 12 || limpo.length === 13)) {
    ddiEncontrado = '55';
  }
  
  if (ddiEncontrado && limpo.startsWith(ddiEncontrado)) {
    limpo = limpo.slice(ddiEncontrado.length);
  }
  
  if (limpo.length === 10) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 6)}-${limpo.slice(6)}`;
  } else if (limpo.length === 11) {
    return `${ddiEncontrado ? '+' + ddiEncontrado + ' ' : ''}(${limpo.slice(0, 2)}) ${limpo.slice(2, 7)}-${limpo.slice(7)}`;
  }
  
  return numero;
};

const contactToAtendimentoContato = (contact: Contact): AtendimentoContato => ({
  id: contact.id,
  nome: contact.nome || '',
  ddi: contact.ddi || '55',
  numero: contact.tipo === 'email'
    ? (contact.email || '')
    : (contact.numero ? formatarNumeroComDddParenteses(contact.numero, contact.ddi) : ''),
  remote_jid: '',
  canal: 'whatsapp',
  foto_perfil: contact.foto_perfil,
  cliente_id: contact.cliente_id,
  is_grupo: false,
  bot_desativado: contact.bot_desativado ?? false,
  tags: contact.tags,
  cliente: contact.cliente
    ? {
        id: contact.cliente.id,
        nome: contact.cliente.nome,
      }
    : undefined,
});

interface ClientContactsProps {
  clientId: number;
}

const ClientContacts: React.FC<ClientContactsProps> = ({ clientId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { can } = usePermissions();
  const podeCriarNovoTicket = can('atendimento.criar_novo_ticket');

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [novoAtendimentoAberto, setNovoAtendimentoAberto] = useState(false);
  const [ticketAtendimentoAtivo, setTicketAtendimentoAtivo] = useState<AtendimentoTicket | null>(null);
  const [modalIniciarAberto, setModalIniciarAberto] = useState(false);
  const [contatoAtendimentoSelecionado, setContatoAtendimentoSelecionado] = useState<Contact | null>(null);
  const [conexoes, setConexoes] = useState<AtendimentoConexao[]>([]);
  const [setorId, setSetorId] = useState<number | null>(null);
  const [loadingConexoes, setLoadingConexoes] = useState(false);
  const [iniciandoId, setIniciandoId] = useState<number | null>(null);
  const [erroModal, setErroModal] = useState('');
  const [contatoDetalhes, setContatoDetalhes] = useState<AtendimentoContato | null>(null);

  const loadTicketAtendimentoAtivo = useCallback(async () => {
    if (!podeCriarNovoTicket) return;
    try {
      const tickets = await atendimentoService.getTickets({ cliente_id: clientId }) as AtendimentoTicket[];
      const ticketAtivo = (tickets || []).find(
        (ticket) => ticket.status !== 'fechado' && ticket.status !== 'fechado_bot'
      ) || null;
      setTicketAtendimentoAtivo(ticketAtivo);
    } catch {
      setTicketAtendimentoAtivo(null);
    }
  }, [clientId, podeCriarNovoTicket]);

  useEffect(() => {
    loadContacts();
  }, [clientId]);

  useEffect(() => {
    loadTicketAtendimentoAtivo();
  }, [loadTicketAtendimentoAtivo]);

  const loadContacts = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await contactService.getByClientId(clientId);
      setContacts(response.data || []);
    } catch (err: any) {
      console.error('Erro ao carregar contatos:', err);
      setError('Erro ao carregar contatos');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (contact?: Contact) => {
    setEditingContact(contact || null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
  };

  const handleFormSubmit = async (data: Partial<Contact>) => {
    try {
      setLoading(true);
      setError(null);

      if (editingContact) {
        // Editar contato existente
        await contactService.update(editingContact.id, data);
        enqueueSnackbar('Contato atualizado com sucesso!', { variant: 'success' });
      } else {
        // Criar novo contato
        await contactService.create(data as any);
        enqueueSnackbar('Contato criado com sucesso!', { variant: 'success' });
      }

      handleCloseDialog();
      loadContacts();
    } catch (err: any) {
      console.error('Erro ao salvar contato:', err);

      // Só mostrar erro aqui se não for erro de validação (que será tratado pelo formulário)
      if (!err.response?.data?.errors) {
        // Erro geral não relacionado à validação
        setError(err.response?.data?.message || err.message || 'Erro ao salvar contato');
      }

      throw err; // Re-throw para que o formulário possa lidar com o erro
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId: number) => {
    if (window.confirm('Tem certeza que deseja excluir este contato?')) {
      try {
        setLoading(true);
        setError(null);

        await contactService.delete(contactId);
        enqueueSnackbar('Contato excluído com sucesso!', { variant: 'success' });
        loadContacts();
      } catch (err: any) {
        console.error('Erro ao excluir contato:', err);
        setError('Erro ao excluir contato');
        enqueueSnackbar('Erro ao excluir contato', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOpenSmsDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setSmsDialogOpen(true);
  };

  const handleCloseSmsDialog = () => {
    setSmsDialogOpen(false);
    setSelectedContact(null);
  };

  const handleOpenEmailDialog = (contact: Contact) => {
    setSelectedContact(contact);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setSelectedContact(null);
  };

  const getContactIcon = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return <Email color="primary" />;
      case 'fixo':
        return <Phone color="primary" />;
      case 'celular':
        return <ContactPhone color="primary" />;
      default:
        return <ContactPhone color="primary" />;
    }
  };

  const getContactValue = (contact: Contact) => {
    switch (contact.tipo) {
      case 'email':
        return contact.email;
      case 'fixo':
        return contact.numero ? formatarNumeroComDddParenteses(contact.numero, contact.ddi) : '';
      case 'celular':
        return contact.numero ? formatarNumeroComDddParenteses(contact.numero, contact.ddi) : '';
      default:
        return '';
    }
  };

  const handleNovoAtendimentoSuccess = (ticket: AtendimentoTicket) => {
    setNovoAtendimentoAberto(false);
    setTicketAtendimentoAtivo(ticket);
    navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
  };

  const handleVerAtendimento = () => {
    if (!ticketAtendimentoAtivo) return;
    navigate('/atendimento/atendimentos', { state: { ticketId: ticketAtendimentoAtivo.id } });
  };

  const contatosWhatsapp = contacts.filter((contact) => contact.is_whatsapp);
  const conexoesDisponiveis = conexoes.filter((conexao) => conexao.ativo && conexao.tipo === 'whatsapp');

  const abrirDetalhesContato = (contact: Contact) => {
    setContatoDetalhes(contactToAtendimentoContato(contact));
  };

  const abrirModalIniciar = async (contact: Contact) => {
    setContatoAtendimentoSelecionado(contact);
    setModalIniciarAberto(true);
    setSetorId(null);
    setErroModal('');
    setLoadingConexoes(true);
    try {
      const data = await atendimentoService.getConexoes();
      setConexoes(data);
    } catch (error: any) {
      setErroModal(error?.response?.data?.error || error?.message || 'Erro ao carregar conexões.');
    } finally {
      setLoadingConexoes(false);
    }
  };

  const iniciarAtendimento = async (conexao: AtendimentoConexao) => {
    if (!contatoAtendimentoSelecionado) return;
    setIniciandoId(conexao.id);
    setErroModal('');
    try {
      const { ticket } = await atendimentoService.iniciarTicket(contatoAtendimentoSelecionado.id, conexao.id, setorId);
      setModalIniciarAberto(false);
      setTicketAtendimentoAtivo(ticket);
      navigate('/atendimento/atendimentos', { state: { ticketId: ticket.id } });
    } catch (error: any) {
      setErroModal(error?.response?.data?.error || error?.message || 'Erro ao iniciar atendimento.');
    } finally {
      setIniciandoId(null);
    }
  };

  const formatContactType = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return 'Email';
      case 'fixo':
        return 'Fixo';
      case 'celular':
        return 'Celular';
      default:
        return tipo;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
          Contatos do Cliente
        </Typography>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
          {podeCriarNovoTicket && (
            ticketAtendimentoAtivo ? (
              <Button
                variant="contained"
                startIcon={<ChatIcon />}
                onClick={handleVerAtendimento}
                size="small"
                color="success"
                sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
              >
                Ver Atendimento
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<ChatIcon />}
                onClick={() => setNovoAtendimentoAberto(true)}
                size="small"
                sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
              >
                Iniciar Atendimento
              </Button>
            )
          )}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            size="small"
            sx={{ fontSize: '0.8rem', px: 1.5, py: 0.5 }}
          >
            Novo Contato
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, fontSize: '0.8rem' }}>
          {error}
        </Alert>
      )}

      {/* Tabela de Contatos */}
      <TableContainer component={Paper} elevation={1} sx={themedTableContainerSx(theme)}>
        <Table size="small">
          <TableHead>
            <TableRow sx={tableHeadRowSx(theme)}>
              <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem', width: 48 }}>Foto</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Tipo</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Nome</TableCell>
              <TableCell sx={{ py: 0.75, fontSize: '0.8rem' }}>Contato</TableCell>
              <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem' }}>WhatsApp</TableCell>
              <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem' }}>Padrão</TableCell>
              <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem' }}>Notificações</TableCell>

              <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow key={contact.id} hover>
                <TableCell align="center" sx={{ py: 0.5, px: 1 }}>
                  <Tooltip title="Ver detalhes do contato">
                    <Avatar
                      src={atendimentoService.getAvatarUrl(contact.foto_perfil)}
                      onClick={() => abrirDetalhesContato(contact)}
                      sx={{
                        width: 28,
                        height: 28,
                        fontSize: '0.65rem',
                        mx: 'auto',
                        cursor: 'pointer',
                        transition: 'transform 0.15s',
                        '&:hover': { opacity: 0.85, transform: 'scale(1.08)' },
                      }}
                    >
                      {!contact.foto_perfil && getInitials(contact.nome)}
                    </Avatar>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ py: 0.5, px: 1.5 }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {getContactIcon(contact.tipo)}
                    <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                      {formatContactType(contact.tipo)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell onClick={() => abrirDetalhesContato(contact)} sx={{ py: 0.5, px: 1.5, cursor: 'pointer' }}>{contact.nome}</TableCell>

                <TableCell onClick={() => abrirDetalhesContato(contact)} sx={{ whiteSpace: 'nowrap', py: 0.5, px: 1.5, cursor: 'pointer' }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {contact.tipo !== 'email' && contact.numero && (() => {
                      const country = obterDadosPais(contact.numero, contact.ddi);
                      return country ? (
                        <Tooltip title={country.label}>
                          <img
                            loading="lazy"
                            width="16"
                            src={new URL(`../../assets/countries/w20/${country.code.toLowerCase()}.png`, import.meta.url).href}
                            alt={country.label}
                            style={{ borderRadius: 2 }}
                          />
                        </Tooltip>
                      ) : null;
                    })()}
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {getContactValue(contact)}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell align="center" sx={{ py: 0.5, px: 1.5 }}>
                  {contact.tipo === 'celular' && contact.is_whatsapp ? (
                    <Chip
                      icon={<WhatsApp sx={{ fontSize: '0.9rem' }} />}
                      label="Sim"
                      color="success"
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  ) : (
                    <Chip
                      label="Não"
                      color="default"
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </TableCell>

                <TableCell align="center" sx={{ py: 0.5, px: 1.5 }}>
                  {contact.is_default ? (
                    <StarIcon color="warning" />
                  ) : (
                    <StarBorderIcon color="disabled" />
                  )}
                </TableCell>

                <TableCell align="center" sx={{ py: 0.5, px: 1.5 }}>
                  {contact.recebe_notificacao ? (
                    <Chip
                      icon={<Notifications sx={{ fontSize: '0.9rem' }} />}
                      label="Sim"
                      color="primary"
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  ) : (
                    <Chip
                      label="Não"
                      color="default"
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </TableCell>



                <TableCell align="center" sx={{ whiteSpace: 'nowrap', py: 0.5, px: 1.5 }}>
                  {podeCriarNovoTicket && contact.is_whatsapp && contact.tipo === 'celular' && (
                    <Tooltip title="Iniciar atendimento">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => abrirModalIniciar(contact)}
                        sx={{ p: 0.5 }}
                      >
                        <ChatIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {contact.is_whatsapp && contact.tipo === 'celular' && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenSmsDialog(contact)}
                      color="success"
                      title="Enviar SMS"
                      sx={{ p: 0.5 }}
                    >
                      <Send sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  )}
                  {contact.tipo === 'email' && (
                    <IconButton
                      size="small"
                      onClick={() => handleOpenEmailDialog(contact)}
                      color="info"
                      title="Enviar Email"
                      sx={{ p: 0.5 }}
                    >
                      <Email sx={{ fontSize: '1rem' }} />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(contact)}
                    color="primary"
                    title="Editar"
                    sx={{ p: 0.5 }}
                  >
                    <Edit sx={{ fontSize: '1rem' }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(contact.id)}
                    color="error"
                    title="Excluir"
                    sx={{ p: 0.5 }}
                  >
                    <Delete sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}

            {contacts.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 2 }}>
                  <Typography color="text.secondary" sx={{ fontSize: '0.9rem' }}>
                    Nenhum contato encontrado
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* FAB para mobile */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: 86, // Footer height (70px) + spacing (16px) = 86px
            right: 16,
            zIndex: theme.zIndex.appBar - 1, // Abaixo do FAB do dashboard mas acima do footer
          }}
          onClick={() => handleOpenDialog()}
        >
          <Add />
        </Fab>
      )}

      {/* Formulário de Contato */}
      <ContactForm
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleFormSubmit}
        editingContact={editingContact}
        clientId={clientId}
        loading={loading}
      />

      {/* Modal de Envio de SMS */}
      {selectedContact && (
        <EnviarWhatsappMsgForm
          open={smsDialogOpen}
          onClose={handleCloseSmsDialog}
          destinatario={getContactValue(selectedContact) || ''}
          destinatarioNome={`${formatContactType(selectedContact.tipo)}`}
          loading={loading}
          contatoId={selectedContact.id}
        />
      )}

      {/* Modal de Envio de Email */}
      {selectedContact && (
        <EnviarEmailForm
          open={emailDialogOpen}
          onClose={handleCloseEmailDialog}
          destinatario={getContactValue(selectedContact) || ''}
          destinatarioNome={`${formatContactType(selectedContact.tipo)}`}
          loading={loading}
        />
      )}

      <NovoAtendimentoClienteDialog
        open={novoAtendimentoAberto && podeCriarNovoTicket}
        onClose={() => setNovoAtendimentoAberto(false)}
        onSuccess={handleNovoAtendimentoSuccess}
        contatosCliente={contatosWhatsapp}
      />

      <ContatoAtendimentoDetalhesDialog
        open={Boolean(contatoDetalhes)}
        contato={contatoDetalhes}
        onClose={() => setContatoDetalhes(null)}
      />

      <Dialog open={modalIniciarAberto} onClose={() => !iniciandoId && setModalIniciarAberto(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <ChatIcon color="success" />
            <Box>
              <Typography variant="h6" fontWeight={700}>Iniciar atendimento</Typography>
              {contatoAtendimentoSelecionado && (
                <Typography variant="body2" color="text.secondary">
                  {contatoAtendimentoSelecionado.nome || getContactValue(contatoAtendimentoSelecionado)}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {erroModal && <Alert severity="error" sx={{ m: 2 }}>{erroModal}</Alert>}

          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <SetoresAllSelect
              value={setorId}
              onChange={setSetorId}
              disabled={!!iniciandoId}
              label="Setor"
              placeholder="Selecione o setor"
            />
          </Box>

          {loadingConexoes ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress size={28} />
            </Box>
          ) : conexoesDisponiveis.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Nenhuma conexão WhatsApp ativa encontrada.
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {conexoesDisponiveis.map((cx, index) => {
                const conectada = cx.status === 'conectado';
                const statusColor = conectada
                  ? theme.palette.success.main
                  : cx.status === 'aguardando_qr'
                    ? theme.palette.warning.dark
                    : cx.status === 'conectando'
                      ? theme.palette.info.main
                      : theme.palette.error.main;

                return (
                  <React.Fragment key={cx.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton disabled={!!iniciandoId || !conectada} onClick={() => iniciarAtendimento(cx)} sx={{ py: 1.3 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: conectada ? alpha(theme.palette.success.main, 0.15) : 'action.hover' }}>
                          <WhatsApp sx={{ color: conectada ? '#25d366' : 'text.disabled' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" fontWeight={700}>{cx.nome}</Typography>
                            <Chip
                              size="small"
                              label={cx.status}
                              icon={<CircleIcon sx={{ fontSize: '8px !important', color: `${statusColor} !important` }} />}
                              sx={{ height: 20, fontSize: '0.65rem' }}
                            />
                          </Box>
                        }
                        secondary={cx.numero ? `Número: ${cx.numero}` : 'Sem número vinculado'}
                      />
                      {iniciandoId === cx.id && <CircularProgress size={20} />}
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalIniciarAberto(false)} disabled={!!iniciandoId}>Cancelar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientContacts; 